import { SerializedPublicAccountTrackerType } from 'fym-common/src/db/serializersTypes';

/**
 * What a pasted snippet turned out to be.
 *
 * The rules below mirror the server (`recognizeTrackerType` and
 * `getTrackerUniqueData` in `Accounts/controllers/utils.ts`) on purpose. The
 * paste box tells the artist what it found before they press Create, so the
 * answer it gives must be the answer the server gives. Anything looser would
 * promise a tracker the server then refuses.
 */
export enum TrackerDetectionKind {
  /** Nothing pasted yet. */
  empty = 'empty',
  /** A provider and its id were read out of the snippet. */
  recognised = 'recognised',
  /** A tracker we know by name, but do not run. */
  unsupported = 'unsupported',
  /** No tracking id in there at all. */
  unreadable = 'unreadable',
}

/** Trackers we recognise by sight and still cannot add. */
export enum UnsupportedTracker {
  tikTokPixel = 'tikTokPixel',
  googleAdsTag = 'googleAdsTag',
  matomo = 'matomo',
}

export type TrackerDetection =
  | { kind: TrackerDetectionKind.empty }
  | { kind: TrackerDetectionKind.recognised; type: SerializedPublicAccountTrackerType; trackerId: string }
  | { kind: TrackerDetectionKind.unsupported; tracker: UnsupportedTracker }
  | { kind: TrackerDetectionKind.unreadable };

/** Brand names. They read the same in every language, so they are not translated. */
export const unsupportedTrackerNames: Record<UnsupportedTracker, string> = {
  [UnsupportedTracker.tikTokPixel]: 'TikTok Pixel',
  [UnsupportedTracker.googleAdsTag]: 'Google Ads tag',
  [UnsupportedTracker.matomo]: 'Matomo tracker',
};

function recognizeTrackerType(snippet: string): SerializedPublicAccountTrackerType | undefined {
  if (snippet.includes('facebook')) {
    return SerializedPublicAccountTrackerType.facebookPixel;
  }
  if (snippet.includes('analytics.com/ga.js')) {
    return SerializedPublicAccountTrackerType.googleAnalyticsLegacy;
  }
  if (snippet.includes('analytics.com/analytics.js')) {
    return SerializedPublicAccountTrackerType.googleAnalytics;
  }
  if (snippet.includes('googletagmanager.com/gtm.js') || snippet.includes('GTM-')) {
    return SerializedPublicAccountTrackerType.googleTagManager;
  }
  if (snippet.includes('googletagmanager.com/gtag') || snippet.includes(`gtag('config'`)) {
    return SerializedPublicAccountTrackerType.googleGlobalSite;
  }
  if (snippet.includes('plausible')) {
    return SerializedPublicAccountTrackerType.plausible;
  }
  return undefined;
}

/** An empty capture counts as no match, the way the server's `if (!trackerId)` does. */
function firstGroup(snippet: string, pattern: RegExp): string | undefined {
  const group = snippet.match(pattern)?.[1];
  return group === undefined || group === '' ? undefined : group;
}

function readTrackerId(snippet: string, type: SerializedPublicAccountTrackerType): string | undefined {
  const parsedSnippet = snippet.replace(/(\n|\s)/g, '');
  switch (type) {
    case SerializedPublicAccountTrackerType.facebookPixel:
      return firstGroup(parsedSnippet, /id=(.*?)(&|'|")/) ?? firstGroup(parsedSnippet, /fbq\('init','(.*?)('|")/);
    case SerializedPublicAccountTrackerType.googleAnalyticsLegacy:
      return firstGroup(parsedSnippet, /_setAccount','(.*?)('|")/);
    case SerializedPublicAccountTrackerType.googleAnalytics:
      return firstGroup(parsedSnippet, /create','(.*?)('|")/);
    case SerializedPublicAccountTrackerType.googleTagManager: {
      const fromUrl =
        firstGroup(parsedSnippet, /id=(.*?)(&|'|")/) ?? firstGroup(parsedSnippet, /dataLayer','(.*?)('|")/);
      if (fromUrl !== undefined) {
        return fromUrl;
      }
      const container = firstGroup(parsedSnippet, /GTM-(.*?)('|")/);
      return container === undefined ? undefined : `GTM-${container}`;
    }
    case SerializedPublicAccountTrackerType.googleGlobalSite:
      return firstGroup(parsedSnippet, /id=(.*?)(&|'|")/) ?? firstGroup(parsedSnippet, /'config','(.*?)('|")/);
    case SerializedPublicAccountTrackerType.plausible: {
      // The server stores the domain and the script host together, so a paste
      // that carries only one of them is not enough.
      const domain = firstGroup(parsedSnippet, /data-domain="(.*?)(")/);
      const url = firstGroup(parsedSnippet, /src="(.*?)\/js\//);
      return domain !== undefined && url !== undefined ? domain : undefined;
    }
    default:
      return undefined;
  }
}

function recognizeUnsupportedTracker(snippet: string): UnsupportedTracker | undefined {
  if (/ttq\.load|tiktok/i.test(snippet)) {
    return UnsupportedTracker.tikTokPixel;
  }
  if (/AW-\d{9,11}/i.test(snippet)) {
    return UnsupportedTracker.googleAdsTag;
  }
  if (/matomo|_paq/i.test(snippet)) {
    return UnsupportedTracker.matomo;
  }
  return undefined;
}

/** Reads a pasted snippet the way the server reads it. */
export function detectTracker(snippet: string): TrackerDetection {
  if (snippet.trim() === '') {
    return { kind: TrackerDetectionKind.empty };
  }
  const type = recognizeTrackerType(snippet);
  if (type !== undefined) {
    const trackerId = readTrackerId(snippet, type);
    if (trackerId !== undefined && trackerId !== '') {
      return { kind: TrackerDetectionKind.recognised, type, trackerId };
    }
  }
  const unsupported = recognizeUnsupportedTracker(snippet);
  if (unsupported !== undefined) {
    return { kind: TrackerDetectionKind.unsupported, tracker: unsupported };
  }
  return { kind: TrackerDetectionKind.unreadable };
}
